import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { TicketLogo, SearchIcon, MenuIcon, XIcon, ChevronDownIcon, SunIcon, MoonIcon } from './icons';
import CurrencyMenu from './CurrencyMenu';
import LanguageMenu from './LanguageMenu';
import { HeartIcon } from './SaveButton';
import { ToastHost } from './Toast';
import { useT } from '../i18n';
import { NAV } from '../data/nav';
import { localDay, useSaved } from '../lib/saved';

/** Header height (h-16) — the threshold the hero search has to pass. */
const HEADER_H = 64;

interface MastheadProps {
  onOpenSearch: () => void;
  dark: boolean;
  onToggleTheme: () => void;
}

/**
 * Site header: a white bar with three things on it — the logo, the
 * categories, and a cluster of real controls on the right (search, theme,
 * region). The first version of this header had nothing "on": grey text,
 * no controls, and the theme toggle hidden inside the region menu, where
 * nobody found it. Now the controls are visible and the nav has weight.
 *
 * Search: the hero owns the search box on the homepage, so above the fold
 * the header shows only a search *button*; once the hero search scrolls
 * under the header (or on any page without one) it becomes a field. Both
 * live in one fixed-width slot, so the controls beside it never move.
 *
 * Each category opens onto the level below it (Sports → NFL, MLB, NHL, NBA;
 * Concerts → the busiest artists), built from the live catalog in
 * data/nav.ts so a menu entry can never point at an empty page. The label
 * stays a link, so the menu never gets between a visitor and /sports.
 * "This weekend" is a time filter, not a category, and is drawn as a pill
 * so it reads as one.
 *
 * ⌘/Ctrl-K still opens search (App owns the listener); it isn't advertised.
 */
const Masthead = ({ onOpenSearch, dark, onToggleTheme }: MastheadProps) => {
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [menu, setMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const t = useT();
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef(0);
  const { pathname } = useLocation();
  // Saved events still ahead, on this device (src/lib/saved.ts): [] on the server and while
  // hydrating, so the count appears after mount; the heart itself is always there, so nothing pops in.
  const saved = useSaved();
  const savedCount = useMemo(() => {
    const today = localDay();
    return saved.filter((s) => s.date >= today).length;
  }, [saved]);

  // inline-flex, not a bare inline <a>: the label is a flex <span>, and a block inside an
  // inline box made the highlight 82px tall in a 64px header — the active category's
  // grey pill hung below the bar ("the ugly white thing", owner 2026-10-03).
  const cls = ({ isActive }: { isActive: boolean }) =>
    `inline-flex items-center rounded-md px-3 py-2 text-[14.5px] font-medium transition-colors ${
      isActive ? 'bg-muted text-foreground' : 'text-foreground/80 hover:bg-muted hover:text-foreground'
    }`;
  const pill = ({ isActive }: { isActive: boolean }) =>
    `ml-1 inline-flex items-center gap-2 rounded-full border px-3.5 py-[7px] text-[13.5px] font-medium transition-colors ${
      isActive
        ? 'border-primary/40 bg-primary/10 text-foreground'
        : 'border-border text-foreground/80 hover:border-foreground/30 hover:text-foreground'
    }`;
  const control =
    'relative flex h-9 items-center justify-center rounded-md border border-border bg-card text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground max-md:after:absolute max-md:after:-inset-1';

  // `hidden`: the header slides away while scrolling DOWN past the fold, on
  // small screens only; desktop keeps it pinned (there is room for it there).
  // `compact`: the header search becomes a field once the hero's own search
  // box has passed under the header — or immediately on pages without one.
  //
  // One passive listener does both: a single rect read per scroll event, no
  // writes, so nothing thrashes layout.
  useEffect(() => {
    const hero = document.getElementById('hero-search');
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - last;
      // Near the top the header is always shown. A page change jumps to y=0 before this effect
      // re-reads `last`, so no scroll delta would ever bring it back from a scrolled-down page.
      if (y <= 120) setHidden(false);
      if (Math.abs(delta) > 6) {
        setHidden(y > 120 && delta > 0);
        last = y;
      }
      setCompact(!hero || hero.getBoundingClientRect().bottom < HEADER_H);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  // Sticky bars below the header (.sticky-under-header) move up to the very top
  // while the header is slid away, instead of leaving a 64px gap above them.
  useEffect(() => {
    document.documentElement.dataset.hdr = hidden ? 'hidden' : 'shown';
  }, [hidden]);

  // Navigating closes whatever was open.
  useEffect(() => {
    setMenu(null);
    setOpen(false);
    setMobileOpen(null);
  }, [pathname]);

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(null);
    const onDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menu]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  // A short grace period on leave, so crossing the gap between a label and
  // its panel doesn't snap the panel shut.
  const hold = (label: string | null) => {
    window.clearTimeout(closeTimer.current);
    if (label === null) closeTimer.current = window.setTimeout(() => setMenu(null), 140);
    else setMenu(label);
  };

  return (
    // Opaque, no backdrop-blur: blur(24px) under a sticky header smears and
    // stutters while scrolling on many Windows/low-end GPUs. Card-white on
    // the off-white page, so the bar reads as a bar.
    <header
      className={`sticky top-0 z-40 border-b border-border bg-card transition-transform duration-200 lg:translate-y-0 ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="shell flex h-16 items-center gap-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <TicketLogo className="size-7 text-primary" />
          <span className="text-[17px] font-semibold tracking-tight">
            Tix<span className="text-primary">Info</span>
          </span>
        </Link>

        <nav ref={navRef} className="hidden items-center gap-1.5 lg:flex">
          {NAV.map((s) =>
            s.to === '/this-weekend' ? (
              <NavLink key={s.to} to={s.to} className={pill} end>
                <span className="size-[6px] rounded-full bg-primary" aria-hidden />
                {t('This weekend')}
              </NavLink>
            ) : (
              <div
                key={s.to}
                className="relative"
                onPointerEnter={() => s.children.length > 0 && hold(s.label)}
                onPointerLeave={() => s.children.length > 0 && hold(null)}
                onFocus={() => s.children.length > 0 && hold(s.label)}
              >
                <NavLink
                  to={s.to}
                  className={cls}
                  end
                  aria-expanded={s.children.length > 0 ? menu === s.label : undefined}
                >
                  <span className="flex items-center gap-1">
                    {t(s.label)}
                    {s.children.length > 0 && (
                      <ChevronDownIcon
                        className={`size-3.5 opacity-70 transition-transform duration-150 ${
                          menu === s.label ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </span>
                </NavLink>

                {menu === s.label && s.children.length > 0 && (
                  // pt-2 keeps the panel's hit area touching the label's, so the
                  // pointer never crosses dead space on the way down.
                  <div className="absolute left-0 top-full z-30 pt-2">
                    <div className="w-[272px] overflow-hidden rounded-xl border border-border bg-popover py-1.5 shadow-lg">
                      {s.childLabel && (
                        <p className="px-3 pb-1.5 pt-1 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                          {t(s.childLabel)}
                        </p>
                      )}
                      {s.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="flex items-baseline justify-between gap-3 px-3 py-[7px] text-[13.5px] hover:bg-muted"
                        >
                          <span className="truncate font-medium">{t(c.label)}</span>
                          {c.note && <span className="tabular shrink-0 text-[11.5px] text-muted-foreground">{t(c.note.unit === 'games' ? '{n} games' : '{n} dates', { n: c.note.n })}</span>}
                        </Link>
                      ))}
                      {s.extra && (
                        <div className="mt-1 border-t border-border pt-1">
                          <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{t(s.extra.label)}</p>
                          {s.extra.children.map((c) => (
                            <Link key={c.to} to={c.to} className="flex items-baseline px-3 py-[7px] text-[13.5px] hover:bg-muted">
                              <span className="truncate font-medium">{c.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                      <Link
                        to={s.to}
                        className="mt-1 block border-t border-border px-3 pb-1 pt-2 text-[13px] text-muted-foreground hover:text-foreground"
                      >
                        {t('All {section} →', { section: t(s.label).toLowerCase() })}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* One fixed-width slot holds both search states, so the controls
              to its right never move when the field appears. From xl up only:
              between 1024 and 1279 px the categories, this slot and the controls
              did not fit (the bar scrolled sideways), so there the icon below
              opens the same search. */}
          <div className="relative hidden h-9 w-[248px] xl:block">
            <button
              type="button"
              onClick={onOpenSearch}
              tabIndex={compact ? undefined : -1}
              aria-hidden={!compact}
              className={`absolute inset-0 flex items-center gap-2.5 rounded-md border border-border bg-card px-3 text-left text-[13.5px] text-muted-foreground transition-[opacity,transform] duration-200 hover:border-foreground/30 hover:text-foreground ${
                compact ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-0.5 opacity-0'
              }`}
            >
              <SearchIcon className="size-4 shrink-0" />
              <span className="truncate">{t('Search artist, team or event')}</span>
            </button>
            <button
              type="button"
              onClick={onOpenSearch}
              aria-label={t('Search')}
              tabIndex={compact ? -1 : undefined}
              aria-hidden={compact}
              className={`${control} absolute right-0 top-0 w-9 transition-opacity duration-200 ${
                compact ? 'pointer-events-none opacity-0' : 'opacity-100'
              }`}
            >
              <SearchIcon className="size-4" />
            </button>
          </div>

          <button type="button" onClick={onOpenSearch} aria-label={t('Search')} className={`${control} w-9 xl:hidden`}>
            <SearchIcon className="size-4" />
          </button>

          <Link
            to="/saved"
            rel="nofollow"
            aria-label={savedCount ? t('Saved events ({n})', { n: savedCount }) : t('Saved events')}
            title={t('Saved events')}
            className={`${control} relative w-9 ${pathname === '/saved' ? 'border-foreground/30 text-foreground' : ''}`}
          >
            <HeartIcon className="size-4" filled={pathname === '/saved'} />
            {savedCount > 0 && (
              <span
                aria-hidden
                className="tabular absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[10.5px] font-semibold leading-none text-primary-foreground ring-2 ring-card"
              >
                {savedCount > 99 ? '99+' : savedCount}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={dark ? t('Switch to light mode') : t('Switch to dark mode')}
            aria-pressed={dark}
            title={dark ? t('Light mode') : t('Dark mode')}
            className={`${control} w-9`}
          >
            {dark ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
          </button>

          <div className="hidden items-center gap-2 sm:flex">
            <LanguageMenu />
            <CurrencyMenu />
          </div>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={t('Menu')}
            aria-expanded={open}
            aria-controls="mobile-nav"
            className={`${control} w-9 lg:hidden`}
          >
            {open ? <XIcon className="size-4" /> : <MenuIcon className="size-4" />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-nav" className="max-h-[72vh] overflow-y-auto border-t border-border bg-card lg:hidden">
          <div className="shell flex flex-col gap-1 py-3">
            {NAV.map((s) => (
              <div key={s.to}>
                <div className="flex items-center">
                  <NavLink to={s.to} className={(p) => `${cls(p)} min-h-11 flex-1 py-2.5`} end onClick={() => setOpen(false)}>
                    {s.to === '/this-weekend' ? t('This weekend') : t(s.label)}
                  </NavLink>
                  {s.children.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setMobileOpen(mobileOpen === s.label ? null : s.label)}
                      aria-label={t('{section} subcategories', { section: t(s.label) })}
                      aria-expanded={mobileOpen === s.label}
                      className="relative flex size-9 items-center justify-center rounded-md text-muted-foreground max-md:after:absolute max-md:after:-inset-1"
                    >
                      <ChevronDownIcon
                        className={`size-4 transition-transform duration-150 ${mobileOpen === s.label ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </div>
                {mobileOpen === s.label && (
                  <div className="mb-1 ml-3 flex flex-col border-l border-border pl-3">
                    {s.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="flex min-h-11 items-center justify-between gap-3 py-2 text-[13.5px] md:min-h-0 md:items-baseline"
                      >
                        <span className="truncate">{t(c.label)}</span>
                        {c.note && <span className="tabular shrink-0 text-[11.5px] text-muted-foreground">{t(c.note.unit === 'games' ? '{n} games' : '{n} dates', { n: c.note.n })}</span>}
                      </Link>
                    ))}
                    {s.extra && (
                      <div className="mt-1 border-t border-border pt-1">
                        <p className="pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{t(s.extra.label)}</p>
                        {s.extra.children.map((c) => (
                          <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="flex min-h-11 items-center py-2.5 text-[13.5px]">
                            <span className="truncate font-medium">{c.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* Currency lives in the bar from sm up; below that it's in here. */}
            <div className="mt-2 flex items-center gap-2 border-t border-border pt-3 sm:hidden">
              <LanguageMenu />
              <CurrencyMenu />
            </div>
          </div>
        </nav>
      )}
      {/* The site's one toast ("Saved on this device…", "Link copied"): portalled into <body>,
          so the header's slide-away transform never carries it along (src/components/Toast.tsx). */}
      <ToastHost />
    </header>
  );
};

export default Masthead;
